import FluxEventEmitter from '../FluxEventEmitter';
import assign           from 'object-assign';
import Flux             from '../Flux';
import Constants        from '../Constants';
import VxqlStore        from './VxqlStore.js';

const CHANGE_EVENTS = {
    tileExpandChange: 'tileExpandChange',
    reloadData:       'reloadData',
};

function getKey(args) {
    return JSON.stringify(args);
}

const _defaultFilter = {
    boughtVideos:    {filter: {mediaType: Constants.Mediathek.MediaType.VIDEO, sourceType: Constants.Mediathek.SourceType.PURCHASED}},
    pinnedVideos:    {filter: {mediaType: Constants.Mediathek.MediaType.VIDEO, sourceType: Constants.Mediathek.SourceType.PINNED}},
    messengerVideos: {filter: {mediaTypes: Constants.MessengerContent.VIDEO, searchString: ""}},
    feedVideos:      {typeGroups: [Constants.Feed.Filter.CLIP_POSTS], bought: true, mode: Constants.Feed.Mode.MIXED},

    boughtPhotos: {filter: {mediaType: Constants.Mediathek.MediaType.PHOTO, sourceType: Constants.Mediathek.SourceType.PURCHASED}},
    pinnedPhotos: {filter: {mediaType: Constants.Mediathek.MediaType.PHOTO, sourceType: Constants.Mediathek.SourceType.PINNED}},

    messengerPhotos: {filter: {mediaTypes: Constants.MessengerContent.BITMAP, searchString: ""}},
    feedPhotos:      {typeGroups: [Constants.Feed.Filter.PHOTOS_POSTS], bought: true, mode: Constants.Feed.Mode.MIXED},

    audio: {filter: {mediaTypes: Constants.MessengerContent.AUDIO, searchString: ""}}
};

let _mediathekData   = {};
const _messengerData = {};
const _feedData      = {};

const MediathekStore = assign({}, FluxEventEmitter.prototype, {
    onTileExpand: function(videoId) {
        MediathekStore.emit(CHANGE_EVENTS.tileExpandChange, videoId);
    },

    addTileExpandListener: function(callback) {
        this.on(CHANGE_EVENTS.tileExpandChange, callback);
    },

    removeTileExpandListener: function(callback) {
        this.removeListener(CHANGE_EVENTS.tileExpandChange, callback);
    },

    getBoughtVideos(first, offset, order, search, callback) {
        const filter = _defaultFilter.boughtVideos;
        getMediaLibraryData({...filter, first, offset, order, search}, callback);
    },

    getPinnedVideos(first, offset, order, search, callback) {
        const filter = _defaultFilter.pinnedVideos;
        getMediaLibraryData({...filter, first, offset, order, search}, callback);
    },

    getMessengerVideos(first, offset, order, search, callback) {
        const videos               = _defaultFilter.messengerVideos;
        videos.filter.searchString = search;
        getMessengerData({...videos, first, offset, order}, callback);
    },

    getFeedVideos(first, after, callback) {
        const filter = _defaultFilter.feedVideos;
        filter.first = first;
        filter.after = after;
        getFeedData(filter, callback);
    },

    getBoughtPhotos(first, offset, order, search, callback) {
        const filter = _defaultFilter.boughtPhotos;
        getMediaLibraryData({...filter, first, offset, order, search}, callback);
    },

    getPinnedPhotos(first, offset, order, search, callback) {
        const filter = _defaultFilter.pinnedPhotos;
        getMediaLibraryData({...filter, first, offset, order, search}, callback);
    },

    getMessengerPhotos(first, offset, order, search, callback) {
        const photos               = _defaultFilter.messengerPhotos;
        photos.filter.searchString = search;
        getMessengerData({...photos, first, offset, order}, callback);
    },

    getFeedPhotos(first, after, callback) {
        const filter = _defaultFilter.feedPhotos;
        filter.first = first;
        filter.after = after;
        getFeedData(filter, callback);
    },

    getAudio(first, offset, order, search, callback) {
        const audio               = _defaultFilter.audio;
        audio.filter.searchString = search;
        getMessengerData({...audio, first, offset, order}, callback);
    },
    clearMediathekCache() {
        _mediathekData = {};
    },
    triggerReload(containerId) {
        MediathekStore.emit(CHANGE_EVENTS.reloadData, containerId);
    },
    addReloadListener(callback) {
        this.on(CHANGE_EVENTS.reloadData, callback);
    },
    removeReloadListener(callback) {
        this.removeListener(CHANGE_EVENTS.reloadData, callback);
    },
    getTotals(callback) {
        const mediaLibraryPromise = new Promise((resolve) => {
            VxqlStore.MediaLibrary.loadMediaTotals((data) => {
                resolve(data);
            });
        });

        const feedPromise = new Promise((resolve) => {
            Flux.Feed.getFeedGuestTotals({guestId: Flux.Guest.getId()}, (data) => {
                resolve({
                    videos: data.clips,
                    photos: data.photos,
                });
            });
        });

        Promise.all([mediaLibraryPromise, feedPromise])
                .then((results) => {
                    if (typeof callback === 'function') {
                        const combinedTotals = results.reduce((acc, result) => {
                            acc.videos += result.videos || 0;
                            acc.photos += result.photos || 0;
                            acc.audio += result.audio || 0;
                            return acc;
                        }, {videos: 0, photos: 0, audio: 0});

                        callback(combinedTotals);
                    }
                })
                .catch((error) => {
                    if (typeof callback === 'function') {
                        callback(null, error); // Pass error to callback
                    }
                });
    }
});

function getMediaLibraryData(args, callback) {
    const key = getKey(args);
    if (_mediathekData[key]) {
        callback(_mediathekData[key]);
    } else {
        VxqlStore.MediaLibrary.loadMediaLibraryData(args, (data) => {
            _mediathekData[key] = data;
            callback(data);
        });
    }
}

function getMessengerData(args, callback) {
    const key = getKey(args);
    if (_messengerData[key]) {
        callback(_messengerData[key]);
    } else {
        loadMessengerData(args, callback);
    }
}

function getFeedData(args, callback) {
    const key = getKey(args);
    if (_feedData[key]) {
        callback(_feedData[key]);
    } else {
        loadFeedData(args, callback);
    }
}

function loadMessengerData(args, callback) {
    Flux.MessengerContent.getMessengerContent(args.first, args.offset, args.filter, args.order, (result) => {
        const data          = result.data.guest.messengerContent;
        const key           = getKey(args);
        _messengerData[key] = data;
        callback(data);
    });
}

function loadFeedData(args, callback) {
    Flux.Feed.getFeedData(args, (res) => {
        const data     = res.data.feed.posts;
        const key      = getKey(args);
        _feedData[key] = data;
        callback(data);
    });
}

export default MediathekStore;

